<div class="min-vh-100">
  <div class="d-flex flex-column gap-4 pb-4">
    <section>
      <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
        <div>
          <h1 class="fw-bold text-x mb-1">Manage Statuses</h1>
          <p class="text-secondary text-xs mb-0">
            Define the valid states for your Supplier Approval workflow.
          </p>
        </div>
        <button
          v-if="workflow_query.workflowId"
          type="button"
          class="btn btn-primary-dark text-xs align-self-start"
          @click="go_to_configure_workflow"
        >
          <i class="bi bi-bezier2 me-2"></i>
          <span>Go To Workflow Logic</span>
        </button>
      </div>

      <div class="card border-0 rounded-4 shadow-sm bg-light-purple bg-opacity-50">
        <div class="card-body p-3 p-lg-4">
          <div v-if="statuses_loading" class="text-secondary text-xs px-2 py-4">
            Loading statuses...
          </div>

          <div v-else class="row g-3">
            <div class="col-12 col-md-6 col-xl-3">
              <article
                class="card border rounded-4 h-100 bg-white shadow-sm cursor-pointer"
                role="button"
                tabindex="0"
                data-bs-toggle="modal"
                data-bs-target="#create_status_modal"
                @click="reset_create_status_form"
                @keydown.enter.prevent="reset_create_status_form"
                @keydown.space.prevent="reset_create_status_form"
              >
                <div class="card-body p-3 d-flex flex-column align-items-center justify-content-center text-center">
                  <div class="entity-type-icon entity-icon-surface d-inline-flex align-items-center justify-content-center mb-3">
                    <i class="bi bi-plus-circle"></i>
                  </div>
                  <p class="fw-bold text-x mb-1">Add New Status</p>
                  <p class="text-secondary text-xs mb-0">Create a custom state</p>
                </div>
              </article>
            </div>

            <div
              v-for="status in statuses"
              :key="status.id"
              class="col-12 col-md-6 col-xl-3"
            >
              <article
                class="card rounded-4 h-100 shadow-sm cursor-pointer"
                :class="selected_status_id === status.id ? 'border-primary border-2' : 'border-0'"
                role="button"
                tabindex="0"
                @click="selected_status_id = status.id"
                @keydown.enter.prevent="selected_status_id = status.id"
                @keydown.space.prevent="selected_status_id = status.id"
              >
                <div class="card-body p-3">
                  <div class="d-flex align-items-start justify-content-between gap-2 mb-3">
                    <span class="badge rounded-pill text-uppercase small" :class="status.badgeClass">
                      {{ status.badge }}
                    </span>
                    <button
                      type="button"
                      class="btn btn-sm"
                      data-bs-toggle="modal"
                      data-bs-target="#edit_status_modal"
                      @click.stop="open_edit_status_modal(status)"
                    >
                      <i class="bi bi-pencil-square"></i>
                    </button>
                  </div>
                  <p class="fw-bold text-x mb-1">{{ status.name }}</p>
                  <p class="text-secondary text-xs mb-3">{{ status.description }}</p>
                  <div class="d-inline-flex align-items-center gap-2 text-xxs fw-semibold" :class="status.noteClass" :style="{ color: status.colorCode }">
                    <span class="rounded-circle d-inline-block" style="width:.4rem;height:.4rem;" :style="{ backgroundColor: status.colorCode }"></span>
                    <span>{{ status.note }}</span>
                  </div>
                </div>
              </article>
            </div>

            <div v-if="!statuses.length" class="col-12">
              <article class="card border-0 rounded-4 h-100 bg-white shadow-sm">
                <div class="card-body p-4 text-center text-secondary text-xs">
                  No existing statuses found.
                </div>
              </article>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>

  <teleport to="body">
    <div
      class="modal fade"
      id="create_status_modal"
      tabindex="-1"
      aria-labelledby="create_status_modal_label"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content bg-white rounded-3">
          <div class="modal-header border-bottom-0 cute-header text-white">
            <h5 class="modal-title text-xs" id="create_status_modal_label">Create New Status</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body py-4">
            <div class="d-flex flex-column gap-3">
              <div>
                <label class="text-secondary text-xxs fw-bold text-uppercase mb-2 d-block">Status Name</label>
                <input
                  v-model="new_status_name"
                  type="text"
                  class="form-control text-xs"
                  placeholder="e.g. Pending"
                />
              </div>

              <div>
                <label class="text-secondary text-xxs fw-bold text-uppercase mb-2 d-block">Color Code</label>
                <div class="d-flex align-items-center gap-3">
                  <input
                    v-model="new_status_color"
                    type="color"
                    class="form-control form-control-color"
                    title="Choose status color"
                  />
                  <input
                    v-model="new_status_color"
                    type="text"
                    class="form-control text-xs"
                    placeholder="#000000"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer border-0 p-3 d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-primary-dark btn-sm text-xs"
              @click="save_status"
              :disabled="status_creating"
            >
              {{ status_creating ? 'Saving...' : 'Create Status' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>

  <teleport to="body">
    <div
      class="modal fade"
      id="edit_status_modal"
      tabindex="-1"
      aria-labelledby="edit_status_modal_label"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content bg-white rounded-3">
          <div class="modal-header border-bottom-0 cute-header text-white">
            <h5 class="modal-title text-xs" id="edit_status_modal_label">Edit Status</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body py-4">
            <div class="d-flex flex-column gap-3">
              <div>
                <label class="text-secondary text-xxs fw-bold text-uppercase mb-2 d-block">Status Name</label>
                <input
                  v-model="edit_status_name"
                  type="text"
                  class="form-control text-xs"
                  placeholder="e.g. Approved"
                />
              </div>

              <div>
                <label class="text-secondary text-xxs fw-bold text-uppercase mb-2 d-block">Color Code</label>
                <div class="d-flex align-items-center gap-3">
                  <input
                    v-model="edit_status_color"
                    type="color"
                    class="form-control form-control-color"
                    title="Choose status color"
                  />
                  <input
                    v-model="edit_status_color"
                    type="text"
                    class="form-control text-xs"
                    placeholder="#00FF00"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer border-0 p-3 d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-primary-dark btn-sm text-xs"
              @click="update_status"
              :disabled="status_updating"
            >
              {{ status_updating ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</div>
